import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import ImageUploadButton from "../../components/admin/ImageUploadButton";

const EMPTY = { slug: "", title_ja: "", title_en: "", detail: "", scope: "", image: "", order: 0, published: true };

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminService() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/services")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("取得に失敗しました"));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY, order: items.length });
    setEditing({});
  };

  const openEdit = (s) => {
    setForm({ slug: s.slug, title_ja: s.title_ja, title_en: s.title_en || "", detail: s.detail, scope: (s.scope || []).join("\n"), image: s.image || "", order: s.order ?? 0, published: s.published });
    setEditing(s);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0, scope: form.scope.split("\n").map((x) => x.trim()).filter(Boolean) };
    try {
      if (editing.id) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`「${s.title_ja}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid="admin-services-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">事業内容の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">SERVICE PAGE MANAGEMENT</p>
        </div>
        <button
          data-testid="service-add-button"
          onClick={openNew}
          className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
        >
          <Plus className="h-4 w-4" />
          新規追加
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">順</th>
              <th className="px-4 py-3">事業名</th>
              <th className="px-4 py-3">英語表記</th>
              <th className="px-4 py-3">公開</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`service-row-${s.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{s.order}</td>
                <td className="px-4 py-3 text-xs font-bold text-white">{s.title_ja}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{s.title_en}</td>
                <td className="px-4 py-3 text-xs">{s.published ? <span className="text-green-400">公開</span> : <span className="text-gray-500">非公開</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`service-edit-${s.id}`} onClick={() => openEdit(s)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`service-delete-${s.id}`} onClick={() => remove(s)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="service-form" onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "事業を編集" : "事業を追加"}</h2>
              <button type="button" data-testid="service-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Field label="事業名（日本語）*">
              <input data-testid="service-form-title-ja" required value={form.title_ja} onChange={set("title_ja")} className={inputCls} placeholder="土木工事" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="事業名（英語）">
                <input data-testid="service-form-title-en" value={form.title_en} onChange={set("title_en")} className={inputCls} placeholder="CIVIL ENGINEERING" />
              </Field>
              <Field label="スラッグ（英小文字・ページ内リンク用）*">
                <input data-testid="service-form-slug" required value={form.slug} onChange={set("slug")} className={inputCls} placeholder="civil" />
              </Field>
            </div>
            <Field label="詳細説明 *">
              <textarea data-testid="service-form-detail" required rows={4} value={form.detail} onChange={set("detail")} className={`resize-y ${inputCls}`} />
            </Field>
            <Field label="対応業務（1行につき1項目）">
              <textarea data-testid="service-form-scope" rows={4} value={form.scope} onChange={set("scope")} className={`resize-y ${inputCls}`} placeholder={"宅地・事業用地の造成工事\n整地・地ならし"} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="画像URL">
                <div className="flex items-start gap-2">
                  <input data-testid="service-form-image" value={form.image} onChange={set("image")} className={`flex-1 ${inputCls}`} placeholder="https://..." />
                  <ImageUploadButton testid="service-form-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, image: url }))} />
                </div>
              </Field>
              <Field label="表示順（小さいほど先頭）">
                <input data-testid="service-form-order" type="number" value={form.order} onChange={set("order")} className={inputCls} />
              </Field>
            </div>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input data-testid="service-form-published" type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-accent" />
              公開する
            </label>
            <button data-testid="service-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
